import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getExchangeCollectiveData,
  getExchangeCollectiveForecastData,
  getLatestForecastDate,
  getPriceAnalysisData,
  getMonthlyPriceForecast,
} from "../../../Rest_api/RestApi";
import { PriceAnalysisData } from "../../../models/priceAnalysis";
import { COLOR_CODES } from "../../../common";
import { ExchangeColors, ExchangeData } from "../Exchange3/FormatData";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import Loading from "../../../components/Loading";
import { stringToConsistentColor } from "../../../extensions/string";
import { FilterTab } from "../../../components/Filters";

import { DynamicDatePicker } from "../../../components/DatePicker";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { DataTabs } from "../../Hppc/Demand/Data";

export const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const chartSuffixes = [
  {
    suffix: "wt_mcp_rs_kwh",
    displayName: "",
    stackSuffix: "wt_mcp_rs_kwh",
  },
];

export const R_CHART_TYPES = [
  { value: ChartType.Bar, label: "Standard Bar", stackId: undefined },
  { value: ChartType.Bar, label: "Stacked Bar", stackId: "a" },
  { value: ChartType.Line, label: "Line" },
];

let Intervals = [
  {
    label: "Time Slot",
    key: "slot",
    values: ["slot", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "day",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Week",
    key: "week",
    values: ["week"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const PRICE_TYPE_OPTIONS = [
  { key: 'simple_avg', label: 'Simple Avg.' },
  { key: 'wt_avg', label: 'Wt. Avg.' }
];

export const H_CHART_TYPES = [
  { value: ChartType.Bar, label: "Standard Bar", stackId: undefined },
  { value: ChartType.Bar, label: "Stacked Bar", stackId: "a" },
  { value: ChartType.Line, label: "Line" },
];

async function getLatestDateRange() {
  // Get tomorrow's date
  const response = await getLatestForecastDate({
    product: "DAM",
    exchange: "IEX",
  });
  console.log("Backend response:", response);

  // Handle different possible response formats
  const parsedDate = new Date(response);
  // start date should be 15 days back 
  const startDate = new Date(parsedDate);
  startDate.setDate(parsedDate.getDate() - 15);
  return {
    startDate: startDate,
    endDate: parsedDate,
    key: "selection",
  };
}

export function formatDateToLocal(date: Date): string {
  // Check if date is valid
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    console.error("Invalid date passed to formatDateToLocal:", date);
    // Return today's date as fallback
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const PriceForcastingIEXDAM = ({
  selectedTab,
  setSelectedTab,
  dataTab,
  setDataTab,
}: {
  selectedTab: any;
  setSelectedTab: any;
  dataTab: any;
  setDataTab: any;
}) => {
  const [data, setData] = useState<any[]>([]);
  const [priceAnalysisData, setPriceAnalysisData] = useState<PriceAnalysisData[]>([]);
  const [monthlyData, setMonthlyData] = useState<any[]>([]); // For monthly forecast data
  const [selectedSuffix, setSelectedSuffix] = useState([chartSuffixes[0]]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[2]);
  const [chartKeys, setChartKeys] = useState<string[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedPriceType, setSelectedPriceType] = useState(PRICE_TYPE_OPTIONS[1]); // Default to 'wt_avg'
  const [selectedDate, setSelectedDate] = useState<{ value: string }[]>([]);
  const [selectedslot, setSelectedslot] = useState<{ value: string }[]>([]);
  const [uniqueDate, setUniqueDate] = useState([]);
  const [uniqueslot, setUniqueslot] = useState([]);

  const [loading, setLoading] = useState(false);
  const [dates, setDate] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  } | null>(null);
  const [tableData, setTableData] = useState<any[]>([]);

  // Initialize dates from backend
  useEffect(() => {
    const initializeDates = async () => {
      try {
        const dateRange = await getLatestDateRange();

        // Validate the returned dates
        if (
          dateRange &&
          dateRange.startDate instanceof Date &&
          dateRange.endDate instanceof Date &&
          !isNaN(dateRange.startDate.getTime()) &&
          !isNaN(dateRange.endDate.getTime())
        ) {
          setDate(dateRange);
        } else {
          throw new Error("Invalid dates returned from backend");
        }
      } catch (error) {
        console.error("Error fetching latest date range:", error);
        // Fallback to default date range if API fails
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);

        const sevenDaysAgo = new Date(tomorrow);
        sevenDaysAgo.setDate(tomorrow.getDate() - 7);

        setDate({
          startDate: sevenDaysAgo,
          endDate: tomorrow,
          key: "selection",
        });
      }
    };

    initializeDates();
  }, []);

  const _handlefetchData = useCallback(async () => {
    setLoading(true);

    try {
      // For month interval, use the monthly forecast API (doesn't need dates)
      if (selectedInterval.key === 'month') {
        const rangeData = await getMonthlyPriceForecast(selectedPriceType.key as 'simple_avg' | 'wt_avg');
        
        const transformedData = rangeData
          .map((item: any) => ({
            ...item,
            actual_mcp: item.avg_price_actual,
            actual_wt_avg_mcp: item.actual_wt_avg_price,
            month: `${monthNames[item.month - 1]} ${item.year}`,
            monthGroup: monthNames[item.month - 1],
          }))
          .sort(
            (a: { monthGroup: string }, b: { monthGroup: string }) =>
              monthNames.indexOf(a.monthGroup) - monthNames.indexOf(b.monthGroup)
          );

        setMonthlyData(transformedData);
        setData([]);
        setPriceAnalysisData([]);
      } else {
        // For slot, day, week intervals, need dates
        if (!dates) {
          setLoading(false);
          return;
        }

        if (
          !(dates.startDate instanceof Date) ||
          !(dates.endDate instanceof Date)
        ) {
          console.error("Invalid date objects:", dates);
          setLoading(false);
          return;
        }

        if (isNaN(dates.startDate.getTime()) || isNaN(dates.endDate.getTime())) {
          console.error("Dates are NaN:", dates);
          setLoading(false);
          return;
        }

        const startDateStr = formatDateToLocal(dates.startDate);
        const endDateStr = formatDateToLocal(dates.endDate);

        if (startDateStr === "NaN-NaN-NaN" || endDateStr === "NaN-NaN-NaN") {
          console.error("Formatted dates are invalid:", {
            startDateStr,
            endDateStr,
          });
          setLoading(false);
          return;
        }

        // For slot, day, week intervals, use the new price analysis API
        const analysisData = await getPriceAnalysisData({
          interval: selectedInterval.key as 'slot' | 'day' | 'week',
          price_type: selectedPriceType.key as 'simple_avg' | 'wt_avg',
          exchange_name: 'IEX',
          product_name: 'DAM',
          start_date: startDateStr,
          end_date: endDateStr
        });

        setPriceAnalysisData(analysisData.map((item) => ({
          ...item,
          price: item.price / 1000,
          prev_year_price: item.prev_year_price ? item.prev_year_price / 1000 : null,
          rolling_actual_30_day_avgs: item.rolling_actual_30_day_avgs ? item.rolling_actual_30_day_avgs / 1000 : null,
          prev_year_rolling_actual_30_day_avgs: item.prev_year_rolling_actual_30_day_avgs ? item.prev_year_rolling_actual_30_day_avgs / 1000 : null,
          gna_forecasted_price: item.gna_forecasted_price ? item.gna_forecasted_price / 1000 : null,
        })));
        setData([]);
        setMonthlyData([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [dates, selectedInterval.key, selectedPriceType.key]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  useEffect(() => {
    // For month interval, use the monthly forecast data processing logic
    if (selectedInterval.key === 'month') {
      let filteredData = monthlyData;
      const par: ChartDataParams = {
        keys: ["monthGroup"],
        tableKeys: ["monthGroup", "month", "year"],
        groupBy: ["year"],
        volumeKey: ``,
        aggregationKeys: selectedPriceType.key === 'wt_avg' ? ["actual_wt_avg_mcp"] : ["actual_mcp"],
        priceKey: "",
        data: filteredData,
      };
      const cData = FormatChartData(par);
      setChartData(cData.data);
      setChartKeys(cData.keys);
      setTableData(cData.tableData || []);
    } else {
      // For slot/day/week intervals, use the new price analysis data
      // Transform PriceAnalysisData to chart format
      const transformedData = priceAnalysisData.map((item) => {
        const baseData: any = {
          x_axis: item.x_axis,
          price: item.price,
          prev_year_price: item.prev_year_price,
          rolling_actual_30_day_avgs: item.rolling_actual_30_day_avgs,
          prev_year_rolling_actual_30_day_avgs: item.prev_year_rolling_actual_30_day_avgs,
          gna_forecasted_price: item.gna_forecasted_price,
        };

        // Add interval-specific fields
        if (selectedInterval.key === 'slot') {
          baseData.slot = item.x_axis;
          baseData.date = item.second_x_axis || item.x_axis;
          baseData.second_x_axis = item.second_x_axis;
        } else if (selectedInterval.key === 'day') {
          baseData.date = item.x_axis;
        } else if (selectedInterval.key === 'week') {
          baseData.week = item.x_axis;
          baseData.second_x_axis = item.second_x_axis;
        }

        return baseData;
      });

      setChartData(transformedData);
      setTableData(transformedData);
      setChartKeys(['price', 'prev_year_price', 'rolling_actual_30_day_avgs', 'prev_year_rolling_actual_30_day_avgs', 'gna_forecasted_price'].filter(key => {
        return transformedData.some(item => item[key as keyof typeof item] !== null && item[key as keyof typeof item] !== undefined);
      }));
    }
  }, [data, priceAnalysisData, monthlyData, selectedDate, selectedInterval, selectedslot, selectedPriceType.key]);

  const handlexDataKey = useCallback(() => {
    // For month interval, use monthGroup
    if (selectedInterval.key === "month") {
      return "monthGroup";
    }
    // For slot/day/week intervals, use x_axis
    return "x_axis";
  }, [selectedInterval.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[]
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: selectedInterval.isTime
        ? `${key.replace("_", " ").replace("undefined", "")} ${displayName}`
        : displayName,
      legendColor: stringToConsistentColor(`${key}_${suffix}`),
      stroke: stringToConsistentColor(`${key}_${suffix}`),
      unit: suffix.includes("mcp") ? "Rs/kWh" : "KW",
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
    };
  };

  function getLegends(includeActual: boolean | undefined = false) {
    // For month interval, use the monthly forecast legend logic
    if (selectedInterval.key === 'month') {
      const getChartSuffix = () => {
        const baseSuffix = [];
        if (selectedPriceType.key === 'wt_avg') {
          baseSuffix.push({
            label: "",
            value: "actual_wt_avg_mcp",
          });
        } else {
          baseSuffix.push({
            label: "",
            value: "actual_mcp",
          });
        }
        return baseSuffix;
      };

      const createMonthlyChartObject = (
        key: string,
        index: number,
        value: string,
        label: string
      ) => {
        const [barMonth, barYear] = key.split(" ");
        const now = new Date();
        const currentMonth = monthNames[now.getMonth()];
        const currentYear = now.getFullYear().toString();

        const isCurrentMonthYear =
          barMonth === currentMonth && barYear === currentYear;
        const isAug2025 = barMonth === "Aug" && barYear === "2025";

        return {
          dataKey: `${key}_${value}`,
          name: `${isCurrentMonthYear ? "Forecast " : ""}${key
            .replace("DAM", "")
            .replace("_", " ")
            .replace("undefined", "")} ${label}`,
          legendColor: undefined,
          type: ChartType.Bar,
          colorFormatter: (dataIndex: number, dataItem: any) => {
            return isAug2025 ? "#FF5733" : undefined; // Orange color for Aug 2025
          },
        };
      };

      return getChartSuffix().flatMap(({ label, value }) =>
        chartKeys.map(
          (key, index) => createMonthlyChartObject(key, index, value, label) as any
        )
      );
    } else {
      // For slot/day/week intervals, use the new legend structure
      const legends = [
        {
          name: 'Current',
          dataKey: 'price',
          stroke: COLOR_CODES.primary,
          unit: 'Rs/KWh',
          type: ChartType.Line
        }
      ];

      // Only add Prev Year price if we have data for it
      const hasPrevYearData = priceAnalysisData.some(item => item.prev_year_price !== null && item.prev_year_price !== undefined);
      if (hasPrevYearData) {
        legends.push({
          name: 'Prev Year',
          dataKey: 'prev_year_price',
          stroke: '#7CBF00',
          unit: 'Rs/KWh',
          type: ChartType.Line
        });
      }

      // Only add rolling average if we have data for it
      const hasRollingData = priceAnalysisData.some(item => item.rolling_actual_30_day_avgs !== null && item.rolling_actual_30_day_avgs !== undefined);
      if (hasRollingData) {
        legends.push({
          name: '30-Day Roll. Avg.',
          dataKey: 'rolling_actual_30_day_avgs',
          stroke: '#213E77',
          unit: 'Rs/KWh',
          type: ChartType.Line
        });
        legends.push({
          name: 'Prev Year 30-Day Roll. Avg.',
          dataKey: 'prev_year_rolling_actual_30_day_avgs',
          stroke: '#639FCB',
          unit: 'Rs/KWh',
          type: ChartType.Line
        });
      }

      // Only add GNA forecasted price if we have data for it
      const hasGnaForecastData = priceAnalysisData.some(item => item.gna_forecasted_price !== null && item.gna_forecasted_price !== undefined);
      if (hasGnaForecastData) {
        legends.push({
          name: 'GNA Forecast.',
          dataKey: 'gna_forecasted_price',
          stroke: '#F15CE2',
          unit: 'Rs/KWh',
          type: ChartType.Line
        });
      }

      return legends;
    }
  }

  const tablePar = {
    legends: getLegends(true),
    data: chartData,
    tableData: tableData,
    itemsPerPage: 96,
    unit: selectedInterval.key === 'month' ? "Rs/KWh" : "Rs/KWh",
    extraKeys: selectedInterval.key === 'month' 
      ? ["monthGroup"]
      : (selectedInterval.key === 'slot' || selectedInterval.key === 'week' ? ['x_axis', 'second_x_axis'] : ['x_axis']),
    extraLabels: selectedInterval.key === 'month' 
      ? ["Month"]
      : (selectedInterval.key === 'slot' ? ['Time Slot', 'Date'] : (selectedInterval.key === 'week' ? ['Week', 'Month'] : ['Date'])),
    filename: "Price Forecasting IEX DAM",
    showTotal: false,
  };

  // Render the chart for all intervals
  return (
    <div className="gas-page-container">
      {loading && <Loading />}

      <div className="gas-chart-container">
        <div className="gas-heading gap-x-10">
          <h2 className="chartHeading w-60">DAM Price Forecast (IEX)</h2>
          <div className="flex gap-x-6  justify-end">
            <FilterTab
              options={Intervals.map(interval => interval.label)}
              selected={[selectedInterval.label]}
              onClick={(option) => {
                const interval = Intervals.find(i => i.label === option);
                if (interval) setSelectedInterval(interval);
              }}
            />
            <FilterTab
              options={PRICE_TYPE_OPTIONS.map(option => option.label)}
              selected={[selectedPriceType.label]}
              onClick={(label) => {
                const option = PRICE_TYPE_OPTIONS.find(opt => opt.label === label);
                if (option) setSelectedPriceType(option);
              }}
            />
            <FilterTab
              options={DataTabs.map((item) => item.label)}
              selected={[dataTab.label]}
              onClick={(option, index) => {
                setDataTab(DataTabs[index]);
              }}
            />

            {selectedInterval.key !== 'month' && (
              <DynamicDatePicker
                setDate={(date) => setDate(date[0]!)!}
                defaultDates={dates || undefined}
                minDate="2025-06-06"
              />
            )}

            {/* <button
              onClick={() => {
                dataTab.label === "Chart" &&
                  downloadChartTableCSV(tablePar as any);

                dataTab.label === "Summary" &&
                  downloadSummaryCSV(tablePar as any);

                dataTab.label === "Table" &&
                  downloadChartTableCSV(tablePar as any);
              }}
              className="downloadButton"
            >
              Download Data
            </button> */}
          </div>
        </div>
        <div className="h-full w-full relative">
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              chartContainerHeight="calc(100vh - 250px)"
              showBrush={selectedInterval.key === 'month' ? (chartData.length > 96 ? true : undefined) : (chartData.length > 96 ? true : undefined)}
              brushStart={selectedInterval.key === 'month' ? BrushStart.Start : BrushStart.End}
              isTimeSlot={selectedInterval.key === 'month' ? false : (selectedslot.length < 1 && selectedInterval.key === 'slot')}
              onlyTitle={selectedInterval.key === 'month' ? true : (!(selectedslot.length < 1 && selectedInterval.key === 'slot') && selectedInterval.key !== 'week')}
              barCategoryGap={10}
              unit={selectedInterval.key === 'month' ? "Rs/KWh" : "Rs/KWh"}
              nameGap={40}
              yAxisLabel={selectedInterval.key === 'month' ? "Rs/KWh" : "Rs/KWh"}
              secondYAxisLabel={selectedInterval.key === 'month' ? "Rs/KWh" : undefined}
              yMaxValue={selectedInterval.key === 'month' ? 10 : undefined}
              legends={selectedInterval.key === 'month' ? (getLegends() as any).reverse() : (getLegends() as any)}
              defaultSelectedLegends={selectedInterval.key === 'month' ? (getLegends() as any).filter((legend: any) =>
                legend.name.includes("2025")
              ) : undefined}
              customLegendOrder={selectedInterval.key !== 'month' ? ['Prev Year', 'Current', 'GNA Forecast.', 'Prev Year 30-Day Roll. Avg.', '30-Day Roll. Avg.'] : undefined}
              xDataKey={xDataKey}
              xTick={
                selectedInterval.key === 'month' 
                  ? (value: any, index: number): React.ReactNode => {
                      return renderHourTick(value, 10);
                    }
                  : (selectedInterval.key === 'slot' && selectedslot.length == 0
                    ? (value: any, index: any) => {
                        return renderHourTick(value, 10);
                      }
                    : undefined)
              }
              secondXDataKey={
                selectedInterval.key === 'month' 
                  ? undefined
                  : ((selectedInterval.key === 'slot' && selectedslot.length == 0) || selectedInterval.key === 'week'
                    ? 'second_x_axis'
                    : undefined)
              }
              isPriceForecast={selectedInterval.key === 'month' ? true : undefined}
            />
          )}
          {dataTab.label === "Summary" && (
            selectedInterval.key === 'month' ? (
              <div
                style={{ height: "calc(100vh - 200px)" }}
                className=" overflow-auto"
              >
                <LegendSummaryTable {...(tablePar as any)} />
              </div>
            ) : (
              <LegendSummaryTable {...(tablePar as any)} />
            )
          )}
          {dataTab.label === "Table" && <ChartTable {...(tablePar as any)} />}
           {selectedInterval.key === 'month' && (
             <div className="flex justify-between items-center relative">
               <p
                 style={{ fontSize: "0.85rem", color: "#666", marginTop: "8px" }}
               >
                 <em>Note:</em> (1) MCP reflects a {selectedPriceType.key === 'wt_avg' ? 'weighted' : 'simple'} average, (2) The
                 values for{" "}
                 <strong>
                   {monthNames[new Date().getMonth()]} {new Date().getFullYear()}
                 </strong>{" "}
                 are forecasted estimates.
               </p>
             </div>
           )}
        </div>
      </div>
    </div>
  );
};

export default PriceForcastingIEXDAM;
